// Боковая панель пункта «Контроля» (приёмка 0.3.1, T043; ux.md «Табеля», раздел 4): справа без
// затемнения. Шапка «П-N · название · № пункта», полоса состояния по группе листа, свойства
// «подпись — значение», примечание целиком и решение ИИ, если модель спрашивали (принцип III:
// основание и уверенность решения видны человеку). Пустое — приглушённым текстом.
//
// 005 (T022; FR-011, FR-012, FR-033; макет 6): «Ответственные» — упоминания ячейки, каждое —
// ссылка на карточку записи в «Справочнике» (без ↗: переход внутренний) с бейджем адресата;
// у кого нет почты — «Добавить почту» прямо здесь (право directory.write), у кого адрес не
// подтверждён или нет основного куратора — переход в карточку; «Показать пункты ответственного» —
// фильтр «Контроля» по записи. Ниже — «Пункт уйдёт, когда адресат будет у всех: 1 из 2». Esc при
// открытой форме почты закрывает только её (ux.md, «Слои и Esc»), фокус — на «Добавить почту».
// Пункт без ответственного — блок «Адресат» по zoneAddressee: основной куратор юрлица зоны
// ответственности документа или причина (T047).
//
// 014 (T027; FR-004): пункт протокола в режиме ведения — «Открыть в таблице» ведёт в таблицу
// протокола к этому пункту (/protocols/:id?item=…); вместо листа недели — «Таблица протокола»;
// ответственные — из полей пункта (записи справочника), без строки «Справочник ещё связывает».
// В панели не больше одной сплошной кнопки акцента (README «Табеля»: MUST; T050): пока открыта
// форма почты, акцент — у «Сохранить почту», а «Открыть в таблице» — вторичная.
//
// 027 (FR-022; research, Р9): у пункта без людей ответственный — его контрагенты; 028 (FR-027):
// с подписью впереди «Контрагент: А; Б», как в листе контроля.
import { clarifyReasonLabels, mentionKindLabels } from '@kp/contracts';
import type { Addressee, ControlItem, ControlResponsible } from '@kp/contracts';
import { Button, Drawer, Icon, buttonClass } from '@kp/ui';
import type { LucideIcon } from '@kp/ui';
import {
  AlarmClock,
  CheckCheck,
  CircleHelp,
  Flame,
  ListFilter,
  MailPlus,
  SearchCheck,
  Sparkles,
  Table2,
} from '@kp/ui/icons';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { formatDate, shiftDate } from '../../app/format.ts';
import { HrefLink, controlHref, recordHref } from '../../shell/links.tsx';
import { can, readsDirectory } from '../../shell/permissions.ts';
import { useMe } from '../../shell/session.ts';
import { AddEmailForm } from './AddEmail.tsx';
import { AddresseeBadge, reasonTitle } from '../../app/addressee.tsx';
import { addresseeNote, fixFor, readinessOf, readinessText } from './addressee.tsx';
import type { AddresseeFix } from './addressee.tsx';
import { StatusBadge, isMaintained, itemKey, probabilityText, sourceOf } from './items.tsx';

interface StatusLine {
  readonly text: string;
  readonly tone: 'bad' | 'warn' | 'info';
  readonly icon: LucideIcon;
}

/** Полоса состояния по группе листа: «Просрочено с 06.06.2026», «Горит: срок 20.09.2026». */
export function statusLineOf(item: ControlItem): StatusLine | null {
  const probability = probabilityText(item.evidenceProbability ?? 0);
  // 010 (FR-047, FR-048): закрыт отметкой выполнения правилом — с текстом ячейки, без модели.
  if (item.closedBy === 'mark') {
    return {
      text: item.mark ? `Закрыто по отметке: «${item.mark}»` : 'Закрыто по отметке',
      tone: 'info',
      icon: CheckCheck,
    };
  }
  switch (item.group) {
    case 'overdue':
      // Просрочен со дня после срока: в день срока пункт ещё не просрочен (FR-015).
      return {
        text: item.due ? `Просрочено с ${formatDate(shiftDate(item.due, 1))}` : 'Просрочено',
        tone: 'bad',
        icon: AlarmClock,
      };
    case 'burning':
      return {
        text: item.due ? `Горит: срок ${formatDate(item.due)}` : 'Горит',
        tone: 'bad',
        icon: Flame,
      };
    case 'clarify':
      return {
        text: item.clarify ? `Уточнить: ${clarifyReasonLabels[item.clarify]}` : 'Уточнить',
        tone: 'warn',
        icon: CircleHelp,
      };
    case 'aiReview':
      return { text: `Проверить выполнение · ИИ ${probability}`, tone: 'warn', icon: SearchCheck };
    case 'aiClosed':
      return { text: `Закрыто по примечанию · ИИ ${probability}`, tone: 'info', icon: Sparkles };
    // Закрытый отметкой — выше, по closedBy: группа markClosed у него всегда.
    case 'markClosed':
    case 'transfers':
    case 'quiet':
      return null;
  }
}

/** Вывод решения ИИ: закрыл пункт, оставил на проверку человеку или не повлиял на статус. */
export function evidenceOutcome(item: ControlItem): string {
  if (item.closedBy === 'evidence') return 'Закрыто: пункт выполнен по примечанию';
  if (item.evidenceReview) return 'На проверке: подтвердите выполнение на планёрке';
  return 'Не повлияло: статус по сроку';
}

function Empty({ children }: { children: ReactNode }) {
  return <span className="t-muted">{children}</span>;
}

/** Что исправить у адресата ответственного: у записи, где нет почты или куратора. */
export function fixOf(responsible: ControlResponsible, canWrite: boolean): AddresseeFix {
  const { addressee } = responsible;
  if (addressee.ready) return null;
  return fixFor(addressee.reason, addressee.record ?? responsible.record, canWrite);
}

function ResponsibleLine({
  responsible,
  links,
  canWrite,
  editing,
  onEdit,
  onEditEnd,
}: {
  responsible: ControlResponsible;
  /** Записи справочника открываются по праву directory.read. */
  links: boolean;
  canWrite: boolean;
  editing: boolean;
  onEdit: () => void;
  onEditEnd: () => void;
}) {
  const addRef = useRef<HTMLButtonElement>(null);
  const lineRef = useRef<HTMLLIElement>(null);
  /** Почта записана: «Добавить почту» уйдёт после пересчёта адресата — фокус на строку. */
  const savedRef = useRef(false);
  const wasEditing = useRef(editing);
  // Форма закрыта (Esc, «Отмена» или сохранили) — фокус туда, откуда её открыли (ux.md, раздел 1).
  useEffect(() => {
    if (wasEditing.current && !editing) {
      (savedRef.current ? lineRef.current : addRef.current)?.focus();
      savedRef.current = false;
    }
    wasEditing.current = editing;
  }, [editing]);
  const { record } = responsible;
  const note = addresseeNote(responsible);
  const fix = fixOf(responsible, canWrite);
  const name = record ? (
    links ? (
      <HrefLink className="kp-link" href={recordHref(record)}>
        {record.name}
      </HrefLink>
    ) : (
      <span>{record.name}</span>
    )
  ) : (
    <span>
      {responsible.text}{' '}
      <span className="t-muted">
        · {mentionKindLabels[responsible.kind].toLocaleLowerCase('ru-RU')}
      </span>
    </span>
  );
  return (
    <li ref={lineRef} className="kp-resp" tabIndex={-1}>
      <div className="kp-resp-h">
        <span className="kp-resp-name">{name}</span>
        <AddresseeBadge addressee={responsible.addressee} />
      </div>
      {record && record.name !== responsible.text ? (
        <span className="t-muted kp-resp-note">В ячейке: «{responsible.text}»</span>
      ) : null}
      {note ? <span className="t-muted kp-resp-note">{note}</span> : null}
      {fix?.kind === 'email' && editing ? (
        <AddEmailForm
          accent
          person={fix.person}
          onSaved={() => {
            savedRef.current = true;
            onEditEnd();
          }}
          onCancel={onEditEnd}
        />
      ) : (
        <div className="t-row kp-resp-acts">
          {fix?.kind === 'email' ? (
            <Button ref={addRef} size="sm" icon={MailPlus} onClick={onEdit}>
              Добавить почту
            </Button>
          ) : null}
          {fix?.kind === 'card' && links ? (
            <HrefLink className={buttonClass({ size: 'sm' })} href={fix.href}>
              {fix.label}
            </HrefLink>
          ) : null}
          {record ? (
            <HrefLink
              className={buttonClass({ size: 'sm', variant: 'ghost' })}
              href={controlHref({ responsibleId: record.id })}
            >
              <Icon icon={ListFilter} />
              Показать пункты ответственного
            </HrefLink>
          ) : null}
        </div>
      )}
    </li>
  );
}

/** Ответственные пункта с адресатами и строкой готовности (макет 6). */
function Responsibles({
  item,
  editing,
  onEditingChange,
}: {
  item: ControlItem;
  /** Упоминание, у которого открыта форма почты. */
  editing: string | null;
  onEditingChange: (mentionId: string | null) => void;
}) {
  const me = useMe();
  const canWrite = can(me.permissions, ['directory.write']);
  const links = readsDirectory(me.permissions);
  const responsibles = item.responsibles;
  if (!item.responsible) return null;
  if (!responsibles) {
    // Ответственные ведомого пункта — поля пункта: связывать упоминания справочнику не нужно.
    if (isMaintained(item)) return null;
    return <p className="t-muted kp-empty-line">Справочник ещё связывает ответственных пункта</p>;
  }
  if (responsibles.length === 0) return null;
  return (
    <>
      <ul className="kp-resps" aria-label="Ответственные пункта">
        {responsibles.map((responsible) => (
          <ResponsibleLine
            key={responsible.mentionId}
            responsible={responsible}
            links={links}
            canWrite={canWrite}
            editing={editing === responsible.mentionId}
            onEdit={() => onEditingChange(responsible.mentionId)}
            onEditEnd={() => onEditingChange(null)}
          />
        ))}
      </ul>
      <p className="kp-resp-total">{readinessText(readinessOf(responsibles))}</p>
    </>
  );
}

/** Ключ формы почты у адресата пункта без ответственного — рядом с номерами упоминаний. */
const ZONE_EDIT = 'zone';

/** Пояснение к адресату пункта без ответственного: откуда он или почему его нет. */
export function zoneAddresseeNote(addressee: Addressee, documentNumber: number): string {
  if (addressee.ready) {
    const copy = addressee.copy ? `, копия — ${addressee.copy.name}` : '';
    return `Пункт без ответственного — письмо основному куратору юрлица зоны ответственности${copy}`;
  }
  const target = addressee.record;
  if (addressee.reason === 'no_zone') {
    return `У документа П-${documentNumber} не задано юрлицо — его задают в панели документа в «Документах»`;
  }
  if (addressee.reason === 'no_primary_curator') {
    return target
      ? `У юрлица «${target.name}» нет основного куратора`
      : 'У юрлица зоны ответственности нет основного куратора';
  }
  if (addressee.reason === 'no_email') {
    return target
      ? `Нет почты у основного куратора юрлица зоны: ${target.name}`
      : 'Нет почты у основного куратора юрлица зоны';
  }
  return target
    ? `${reasonTitle(addressee.reason)}: ${target.name}`
    : reasonTitle(addressee.reason);
}

/**
 * Адресат пункта без ответственного (005: FR-033, сценарий 7 п. 4; T047): основной куратор юрлица
 * зоны ответственности документа или причина, почему его нет, и где это исправить — почта на
 * месте, куратор — в карточке юрлица, юрлицо — в панели документа. Статус пункта не меняется.
 */
function ZoneAddressee({
  item,
  editing,
  onEditingChange,
}: {
  item: ControlItem;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}) {
  const me = useMe();
  const canWrite = can(me.permissions, ['directory.write']);
  const links = readsDirectory(me.permissions);
  const addRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(editing);
  useEffect(() => {
    if (wasEditing.current && !editing) addRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);
  const addressee = item.zoneAddressee;
  if (!addressee) return null;
  const target = addressee.ready ? addressee.to : addressee.record;
  const fix = addressee.ready ? null : fixFor(addressee.reason, addressee.record, canWrite);
  const name = target ? (
    links ? (
      <HrefLink className="kp-link" href={recordHref(target)}>
        {target.name}
      </HrefLink>
    ) : (
      <span>{target.name}</span>
    )
  ) : (
    <span className="t-muted">Не определён</span>
  );
  return (
    <div className="kp-resps">
      <div className="kp-resp">
        <div className="kp-resp-h">
          <span className="kp-resp-name">{name}</span>
          <AddresseeBadge addressee={addressee} />
        </div>
        <span className="t-muted kp-resp-note">
          {zoneAddresseeNote(addressee, item.documentNumber)}
        </span>
        {fix?.kind === 'email' && editing ? (
          <AddEmailForm
            accent
            person={fix.person}
            onSaved={() => onEditingChange(false)}
            onCancel={() => onEditingChange(false)}
          />
        ) : (
          <div className="t-row kp-resp-acts">
            {fix?.kind === 'email' ? (
              <Button ref={addRef} size="sm" icon={MailPlus} onClick={() => onEditingChange(true)}>
                Добавить почту
              </Button>
            ) : null}
            {fix?.kind === 'card' && links ? (
              <HrefLink className={buttonClass({ size: 'sm' })} href={fix.href}>
                {fix.label}
              </HrefLink>
            ) : null}
            {!addressee.ready && addressee.reason === 'no_zone' ? (
              <HrefLink className={buttonClass({ size: 'sm' })} href="/documents">
                Открыть «Документы»
              </HrefLink>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export function ItemDrawer({ item, onClose }: { item: ControlItem | null; onClose: () => void }) {
  if (!item) return null;
  return <ItemDrawerPanel item={item} onClose={onClose} />;
}

function ItemDrawerPanel({ item, onClose }: { item: ControlItem; onClose: () => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  // Другая строка — та же панель с другим пунктом: форма почты прежнего пункта закрывается.
  const key = itemKey(item);
  const [shownKey, setShownKey] = useState(key);
  if (key !== shownKey) {
    setShownKey(key);
    setEditing(null);
  }
  const line = statusLineOf(item);
  const transfers = item.transferDates ?? [];
  const maintained = isMaintained(item);
  return (
    <Drawer
      open
      onOpenChange={(open) => !open && onClose()}
      onEscapeKeyDown={(event) => {
        // Esc при открытой форме почты закрывает только её (ux.md, «Слои и Esc»).
        if (editing === null) return;
        event.preventDefault();
        setEditing(null);
      }}
      title={`П-${item.documentNumber} · ${item.documentTitle} · ${sourceOf(item)}`}
      statusTone={line?.tone}
      status={
        line ? (
          <>
            <Icon icon={line.icon} />
            {line.text}
          </>
        ) : undefined
      }
      className="kp-control-drawer"
    >
      {/* Шапка панели — заголовок окна (h2); текст пункта — крупно, как название объекта. */}
      <p className="kp-control-drawer-text">{item.text}</p>
      {item.itemId ? (
        <div className="t-row kp-control-drawer-acts">
          <Link
            to="/protocols/$id"
            params={{ id: item.documentId }}
            search={{ item: item.itemId }}
            className={buttonClass({
              size: 'sm',
              variant: editing === null ? 'primary' : 'secondary',
            })}
          >
            <Icon icon={Table2} />
            Открыть в таблице
          </Link>
        </div>
      ) : null}
      <dl className="t-kv">
        <dt>Протокол</dt>
        <dd>{`П-${item.documentNumber} · ${item.documentTitle}`}</dd>
        {maintained ? (
          <>
            <dt>Источник</dt>
            <dd>Таблица протокола</dd>
          </>
        ) : (
          <>
            <dt>Лист</dt>
            <dd className="t-num">{`${item.sheetName} от ${formatDate(item.sheetDate)}`}</dd>
          </>
        )}
        <dt>Раздел</dt>
        <dd>{item.section || <Empty>Без раздела</Empty>}</dd>
        <dt>Пункт</dt>
        <dd className="t-num">
          {maintained
            ? item.number
              ? `№ ${item.number}`
              : 'Без номера'
            : `${item.number ? `№ ${item.number}` : 'Без номера'} · строка ${item.row}`}
        </dd>
        <dt>Срок</dt>
        <dd className="t-num">{item.due ? formatDate(item.due) : <Empty>Нет срока</Empty>}</dd>
        {item.dueRaw ? (
          <>
            <dt>Ячейка срока</dt>
            <dd className="kp-pre">{item.dueRaw}</dd>
          </>
        ) : null}
        <dt>Переносы</dt>
        <dd className="t-num">
          {transfers.length > 0 ? (
            transfers.map(formatDate).join(' → ')
          ) : item.transfers > 0 ? (
            item.transfers
          ) : (
            <Empty>Нет переносов</Empty>
          )}
        </dd>
        <dt>Ответственный</dt>
        <dd>
          {item.responsible ? (
            <>
              {/* 028 (FR-027): подпись впереди — «(контрагент)» в конце читался как пометка
                  последнего. */}
              {item.responsibleFrom === 'contractor' ? (
                <span className="t-muted">Контрагент: </span>
              ) : null}
              {item.responsible}
            </>
          ) : (
            <Empty>Не указан</Empty>
          )}
        </dd>
        <dt>Статус</dt>
        <dd>
          <StatusBadge item={item} />
        </dd>
      </dl>
      {item.responsible ? (
        <>
          <h3 className="t-sec-h">Ответственные</h3>
          <Responsibles item={item} editing={editing} onEditingChange={setEditing} />
        </>
      ) : item.zoneAddressee ? (
        <>
          <h3 className="t-sec-h">Адресат</h3>
          <ZoneAddressee
            item={item}
            editing={editing === ZONE_EDIT}
            onEditingChange={(open) => setEditing(open ? ZONE_EDIT : null)}
          />
        </>
      ) : null}
      <h3 className="t-sec-h">Примечание</h3>
      {item.comment ? (
        <p className="kp-pre">{item.comment}</p>
      ) : (
        <p className="t-muted kp-empty-line">Нет примечания</p>
      )}
      {item.evidenceProbability != null ? (
        <>
          <h3 className="t-sec-h">Решение ИИ</h3>
          <dl className="t-kv">
            <dt>Вероятность</dt>
            <dd className="t-num">{probabilityText(item.evidenceProbability)}</dd>
            <dt>Вывод</dt>
            <dd>{evidenceOutcome(item)}</dd>
          </dl>
        </>
      ) : null}
    </Drawer>
  );
}
